import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import NvIcon from 'shared/components/nv-icon';
import { copyToClipboard } from 'shared/react-utils';
import SaveToast from 'athena/components/mentoring-program/program-configuration/save-toast';
import { AIEvaluationStatus, AIFeedbackDataRequest, AISubmissionFeedback } from 'redux/schemas/models/submissions';

const FeedbackSection: React.FC<{
  label: string;
  content: string;
  isGenerating: boolean;
  dataQa?: string;
  onRevise?: () => void;
}> = ({ label, content, isGenerating, dataQa, onRevise }) => {
  if (!content && !isGenerating) {
    return null;
  }
  return (
    <div
      className='feedback-section'
      id={label?.toLowerCase()?.replace(/\s+/g, '-')}
      data-qa={dataQa}
    >
      <span>
        <span className='section-label'>{label}: </span>
        <span
          className={isGenerating ? 'ai-generating' : undefined}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: isGenerating ? t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING() : content,
          }}
        />
      </span>
      {onRevise && !isGenerating && (
        <div className='revise-submission-link' data-qa='ai-insights-revise-submission'>
          <button type='button' onClick={onRevise}>
            {t.SUBMISSIONS.AI_ASSIGNMENT.REVISE_SUBMISSION()}
          </button>
        </div>
      )}
    </div>
  );
};

export const AiFeedbackResponse = ({
  generating,
  feedback,
  feedbackDataRequest,
  isLearnerView,
  onRevise,
}: {
  generating: boolean;
  feedbackDataRequest: AIFeedbackDataRequest;
  feedback?: AISubmissionFeedback;
  isLearnerView: boolean;
  onRevise?: () => void;
}) => {
  const [showToast, setShowToast] = useState<boolean>(false);

  const hasSkippedFiles = feedbackDataRequest?.feedbackProcessingStatus?.submissionSkippedFiles?.length > 0;
  const hasExternalLink = feedbackDataRequest?.feedbackProcessingStatus?.hasExternalLink;

  const handleCopyToClipboard = () => {
    copyToClipboard(feedback.feedback).then(() => {
      setShowToast(true);
    }).finally(() => {
      setTimeout(() => {
        setShowToast(false);
      }, 2000);
    });
  };

  const ratingLabels: Record<AIEvaluationStatus, string> = {
    meets_expectations: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.GOOD(),
    needs_improvement: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.NEEDS_IMPROVEMENT(),
    exceeds_expectations: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.AMAZING(),
  };

  const getEvaluationContent = (evaluation: AIEvaluationStatus) => `<span class='section-label'>${ratingLabels[evaluation]}</span> - ${feedback?.reason}`;

  return (
    <>
      {/* Overall Section */}
      <FeedbackSection
        label={t.SUBMISSIONS.AI_ASSIGNMENT.OVERALL()}
        content={feedback?.evaluation ? getEvaluationContent(feedback.evaluation) : undefined}
        isGenerating={generating}
        dataQa='ai-insights-overall'
      />

      {isLearnerView ? (
        <>
          {/* Learner: What You Did Well */}
          <FeedbackSection
            label={t.SUBMISSIONS.AI_ASSIGNMENT.DID_WELL()}
            content={feedback?.workingWell}
            isGenerating={generating}
            dataQa='ai-insights-did-well'
          />
          {/* Learner: Where You Can Grow */}
          <FeedbackSection
            label={t.SUBMISSIONS.AI_ASSIGNMENT.CAN_GROW()}
            content={feedback?.needsImprovement}
            isGenerating={generating}
            dataQa='ai-insights-can-grow'
            onRevise={onRevise}
          />
        </>
      ) : (
        <>
          {/* Admin: What Worked */}
          <FeedbackSection
            label={t.SUBMISSIONS.AI_ASSIGNMENT.WORKED()}
            content={feedback?.workingWell}
            isGenerating={generating}
            dataQa='ai-insights-worked'
          />
          {/* Admin: What Can Be Improved */}
          <FeedbackSection
            label={t.SUBMISSIONS.AI_ASSIGNMENT.IMPROVED()}
            content={feedback?.needsImprovement}
            isGenerating={generating}
            dataQa='ai-insights-improved'
          />
          {/* Admin: Suggested Feedback with Copy */}
          <div className='feedback-section' data-qa='ai-insights-suggested-feedback'>
            <div className='section-label'>
              <div className='feedback-actions'>
                <span>{t.SUBMISSIONS.AI_ASSIGNMENT.SUGGESTED_FEEDBACK()}:</span>
                {showToast ? (
                  <SaveToast status={SaveStatus.COPIED} />
                ) : (
                  <Button
                    variant='link'
                    onClick={handleCopyToClipboard}
                    className='copy-button'
                    disabled={generating}
                    aria-label={t.SUBMISSIONS.AI_ASSIGNMENT.COPY()}
                    data-qa='ai-insights-copy-feedback'
                  >
                    <NvIcon size='xss-smallest' icon='files' />{' '}
                    {t.SUBMISSIONS.AI_ASSIGNMENT.COPY()}
                  </Button>
                )}
              </div>
            </div>
            <div
              className={`section-description ${
                generating ? 'ai-generating' : undefined
              }`}
            >
              {generating
                ? t.SUBMISSIONS.AI_ASSIGNMENT.GENERATING()
                : feedback?.feedback}
            </div>
          </div>
        </>
      )}

      {/* Bottom Note Section */}
      {(hasSkippedFiles || hasExternalLink) && (
        <FeedbackSection
          label={t.SUBMISSIONS.AI_ASSIGNMENT.BOTTOM_NOTE()}
          content={feedbackDataRequest.feedbackProcessingStatus?.fullErrorMessage}
          isGenerating={false}
        />
      )}
    </>
  );
};

export default AiFeedbackResponse;
